/**
 * Utility functions for calculating enrollment progress
 */

export interface ProgressCalculationOptions {
  currentStepIndex: number
  totalSteps: number
  completedSteps: number[]
  skippedSteps: number[]
}

/**
 * Calculate the actual progress percentage based on current step position
 * The progress bar should align exactly with the current step circle
 * 
 * Formula explanation:
 * - If on step 1 (index 0) of 9 steps: (0 + 1) / 9 = 11.11%
 * - If on step 4 (index 3) of 9 steps: (3 + 1) / 9 = 44.44%
 * - If on step 9 (index 8) of 9 steps: (8 + 1) / 9 = 100%
 */
export function calculateProgressPercentage(options: ProgressCalculationOptions): number {
  const { currentStepIndex, totalSteps } = options
  
  // Validate inputs
  if (totalSteps === 0) return 0
  
  // Validate currentStepIndex is within bounds
  if (currentStepIndex < 0) {
    console.warn('Progress calculation: currentStepIndex is negative, clamping to 0')
    return 0
  }
  
  if (currentStepIndex >= totalSteps) {
    console.warn('Progress calculation: currentStepIndex exceeds totalSteps, clamping to 100%', {
      currentStepIndex,
      totalSteps
    })
    return 100
  }
  
  // Progress calculation: (current step number / total steps) * 100
  // Add 1 to currentStepIndex because it's 0-based, but we want step 1, 2, 3, etc.
  const percentage = ((currentStepIndex + 1) / totalSteps) * 100
  
  // Round to nearest whole number for cleaner display
  return Math.round(percentage)
}

/**
 * Get the progress bar width percentage for styling
 * This ensures the bar aligns with step circles
 */
export function getProgressBarWidth(options: ProgressCalculationOptions): string {
  const { currentStepIndex, totalSteps } = options
  
  // Calculate the exact position where the progress bar should end
  // Each step occupies (100 / totalSteps) % of the width
  // The bar should reach the CENTER of the current step's circle
  
  if (totalSteps === 0) return '0%'
  
  // Validate currentStepIndex is within bounds
  let safeCurrentStepIndex = currentStepIndex
  if (currentStepIndex < 0) {
    console.warn('Progress bar: currentStepIndex is negative, clamping to 0')
    safeCurrentStepIndex = 0
  }
  
  if (currentStepIndex >= totalSteps) {
    console.warn('Progress bar: currentStepIndex exceeds totalSteps, clamping to last step', {
      currentStepIndex,
      totalSteps
    })
    safeCurrentStepIndex = totalSteps - 1
  }
  
  // Calculate step width percentage
  const stepWidth = 100 / totalSteps
  
  // Progress should go to the center of the current step
  // For step 1 (index 0): stepWidth * 0.5 = half of first step
  // For step 4 (index 3): stepWidth * 3.5 = middle of 4th step
  const percentage = stepWidth * (safeCurrentStepIndex + 0.5)
  
  // Cap at 100% for the last step
  const cappedPercentage = Math.min(percentage, 100)
  
  return `${cappedPercentage.toFixed(2)}%`
}

/**
 * Alternative: If you want the progress bar to reach the END of current step circle
 * Use this version instead of getProgressBarWidth above
 */
export function getProgressBarWidthToStepEnd(options: ProgressCalculationOptions): string {
  const { currentStepIndex, totalSteps } = options
  
  if (totalSteps === 0) return '0%'
  
  // Calculate step width percentage
  const stepWidth = 100 / totalSteps
  
  // Progress should go to the end of the current step
  // For step 1 (index 0): stepWidth * 1 = end of first step
  // For step 4 (index 3): stepWidth * 4 = end of 4th step
  const percentage = stepWidth * (currentStepIndex + 1)
  
  // Cap at 100%
  const cappedPercentage = Math.min(percentage, 100)
  
  return `${cappedPercentage.toFixed(2)}%`
}

/**
 * Check if a step should be marked as completed in the progress indicator
 */
export function isStepCompletedInProgress(
  stepIndex: number, 
  completedSteps: number[], 
  skippedSteps: number[]
): boolean {
  return completedSteps.includes(stepIndex) || skippedSteps.includes(stepIndex)
}

/**
 * Get the current step's completion status for progress calculation
 */
export function getCurrentStepStatus(
  currentStepIndex: number,
  completedSteps: number[],
  skippedSteps: number[]
): 'completed' | 'skipped' | 'in-progress' | 'pending' {
  if (completedSteps.includes(currentStepIndex)) {
    return 'completed'
  }
  if (skippedSteps.includes(currentStepIndex)) {
    return 'skipped'
  }
  return 'in-progress'
}

/**
 * Get progress percentage text for display
 * Shows percentage based on completed steps vs total
 */
export function getProgressText(options: ProgressCalculationOptions): string {
  const percentage = calculateProgressPercentage(options)
  return `${percentage}%`
}

/**
 * Calculate number of completed steps for analytics
 */
export function getCompletedStepCount(options: ProgressCalculationOptions): number {
  const { completedSteps, skippedSteps } = options
  return completedSteps.length + skippedSteps.length
}